import { useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Separator } from 'src/@/components/ui/separator';
import { Textarea } from 'src/@/components/ui/textarea';

// SETORES precisa de t(), então virou useMemo dentro do componente (ver
// abaixo) em vez de const de módulo — nenhum outro arquivo importa essa
// constante, então mover pra dentro é seguro. Os `value` (enum interno
// persistido em telefonesSetor[].setor) continuam intocados.

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

export default function IdentificacaoSection({ aiAgent, changeModuleConfig, changePersona }) {
    const { t } = useTranslation('settings');
    const identificacao = aiAgent?.config?.module?.realstate?.identificacao || {};
    const telefones = Array.isArray(identificacao.telefonesSetor) ? identificacao.telefonesSetor : [];
    const persona = aiAgent?.config?.promptGuide?.persona || '';

    const setores = useMemo(
        () => [
            { value: 'financeiro', label: t('aiAgents.realstate.identificacao.setorFinanceiro', 'Financeiro') },
            { value: 'administrativo', label: t('aiAgents.realstate.identificacao.setorAdministrativo', 'Administrativo') },
            { value: 'comercial', label: t('aiAgents.realstate.identificacao.setorComercial', 'Comercial') },
            { value: 'suporte', label: t('aiAgents.realstate.identificacao.setorSuporte', 'Suporte') },
            { value: 'outros', label: t('aiAgents.realstate.identificacao.setorOutros', 'Outros') },
        ],
        [t]
    );

    const updateIdentificacao = (patch) => {
        changeModuleConfig({
            identificacao: {
                ...identificacao,
                ...patch,
            },
        });
    };

    const addTelefone = () => {
        updateIdentificacao({ telefonesSetor: [...telefones, { setor: 'financeiro', numero: '' }] });
    };

    const removeTelefone = (index) => {
        updateIdentificacao({ telefonesSetor: telefones.filter((_, i) => i !== index) });
    };

    const updateTelefone = (index, field, value) => {
        updateIdentificacao({
            // Parâmetro renomeado de `t` para `tel`: com useTranslation() agora
            // importado neste arquivo, `t` já é o nome da função de tradução no
            // escopo do componente — reaproveitar `t` aqui criava sombreamento
            // (shadowing) confuso, mesmo não sendo um bug funcional.
            telefonesSetor: telefones.map((tel, i) => (i === index ? { ...tel, [field]: value } : tel)),
        });
    };

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                        <Iconify icon="mdi:office-building-outline" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <CardTitle>{t('aiAgents.realstate.identificacao.title', 'Identificação')}</CardTitle>
                        <CardDescription>{t('aiAgents.realstate.identificacao.subtitle', 'Dados básicos da imobiliária usados pela Lais ao atender clientes.')}</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.realstate.identificacao.personaNameLabel', 'Nome da persona (consultor(a) virtual)')}</Label>
                        <Input
                            value={persona}
                            onChange={(e) => typeof changePersona === 'function' && changePersona(e.target.value)}
                            placeholder={t('aiAgents.realstate.identificacao.personaNamePlaceholder', 'Ex.: Laís, Ester, Ana')}
                            className={INPUT_CLS}
                        />
                        <p className="text-xs text-gray-400">
                            <Trans
                                t={t}
                                i18nKey="aiAgents.realstate.identificacao.personaNameHint"
                                defaults='Como o consultor(a) virtual se apresenta ao cliente. Ex.: "Olá! Eu sou a <strong1>Ester</strong1> da imobiliária...".'
                                components={{ strong1: <strong /> }}
                            />
                        </p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.realstate.identificacao.nomeLabel', 'Nome da imobiliária')}</Label>
                        <Input
                            value={identificacao.nome || ''}
                            onChange={(e) => updateIdentificacao({ nome: e.target.value })}
                            placeholder={t('aiAgents.realstate.identificacao.nomeLabel', 'Nome da imobiliária')}
                            className={INPUT_CLS}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.realstate.identificacao.enderecoLabel', 'Endereço')}</Label>
                        <Textarea
                            value={identificacao.endereco || ''}
                            onChange={(e) => updateIdentificacao({ endereco: e.target.value })}
                            placeholder={t('aiAgents.realstate.identificacao.enderecoPlaceholder', 'Av. Paulista, 1000 – Bela Vista, São Paulo/SP – 01310-100')}
                            className={INPUT_CLS}
                            rows={2}
                        />
                        <p className="text-xs text-gray-400">{t('aiAgents.realstate.identificacao.enderecoHint', 'Formato: Rua, número – bairro, cidade/UF, CEP.')}</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.realstate.identificacao.siteLabel', 'Site')}</Label>
                        <Input
                            type="url"
                            value={identificacao.site || ''}
                            onChange={(e) => updateIdentificacao({ site: e.target.value })}
                            placeholder={t('aiAgents.realstate.identificacao.sitePlaceholder', 'https://www.exemplo.com.br')}
                            className={INPUT_CLS}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.realstate.identificacao.regiaoAtuacaoLabel', 'Região de atuação')}</Label>
                        <Textarea
                            value={identificacao.regiaoAtuacao || ''}
                            onChange={(e) => updateIdentificacao({ regiaoAtuacao: e.target.value })}
                            placeholder={t('aiAgents.realstate.identificacao.regiaoAtuacaoPlaceholder', 'São Paulo, Guarulhos e Osasco')}
                            className={INPUT_CLS}
                            rows={2}
                        />
                        <p className="text-xs text-gray-400">{t('aiAgents.realstate.identificacao.regiaoAtuacaoHint', 'Cidades onde você atende.')}</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.realstate.identificacao.horariosLabel', 'Horários de funcionamento')}</Label>
                        <Textarea
                            value={identificacao.horariosFuncionamento || ''}
                            onChange={(e) => updateIdentificacao({ horariosFuncionamento: e.target.value })}
                            placeholder={t('aiAgents.realstate.identificacao.horariosPlaceholder', 'Seg–Sex: 9h às 18h\nSáb: 9h às 13h\nDom/Feriados: fechado')}
                            className={INPUT_CLS}
                            rows={3}
                        />
                    </div>
                </div>

                <Separator className="bg-gray-200/60" />

                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-900">{t('aiAgents.realstate.identificacao.telefonesTitle', 'Telefones por setor')}</h4>
                            <p className="text-xs text-gray-500">{t('aiAgents.realstate.identificacao.telefonesSubtitle', 'Números de WhatsApp que a Lais pode informar ao cliente.')}</p>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addTelefone}
                            className="rounded-xl border-gray-200 hover:border-[#7C3AED] hover:text-[#7C3AED]"
                        >
                            <Iconify icon="eva:plus-outline" width={16} className="mr-1" />
                            {t('buttons.add', 'Adicionar', { ns: 'common' })}
                        </Button>
                    </div>

                    {telefones.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            <Iconify icon="mdi:phone-outline" width={24} className="text-gray-300" />
                            <p className="text-xs text-gray-500 mt-2">{t('aiAgents.realstate.identificacao.telefonesEmpty', 'Nenhum setor cadastrado')}</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {telefones.map((tel, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <Select
                                        value={tel.setor || 'financeiro'}
                                        onValueChange={(value) => updateTelefone(index, 'setor', value)}
                                    >
                                        <SelectTrigger className={`${INPUT_CLS} w-44`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {setores.map((s) => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Input
                                        value={tel.numero || ''}
                                        onChange={(e) => updateTelefone(index, 'numero', e.target.value)}
                                        placeholder={t('aiAgents.realstate.identificacao.telefoneNumeroPlaceholder', '(11) 99999-9999')}
                                        className={`${INPUT_CLS} flex-1`}
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeTelefone(index)}
                                        className="rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50"
                                    >
                                        <Iconify icon="eva:trash-2-outline" width={16} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
